/**
 * Zod-схемы, типы DTO и зеркала перечислений договора и ссылок на исходные
 * данные объекта — Блок 2 «Трекер объектов», Ф2 · В1 (миграции 048 и 049).
 *
 * Волна В1 — владелец БД: заводит схему и контракт, маршруты приезжают в В2c
 * (договор, группировка «комплекс») и В11 (ссылки на исходные, Ф7).
 *
 * ─── ДВА ОТВЕТА ДОГОВОРА — ЭТО НЕ УДОБСТВО, А ПРАВО ДОСТУПА ───────────────
 *
 * `sum` видят только ADMIN и acts-as-GIP. Поэтому здесь ДВЕ схемы ответа, а не
 * одна с необязательным полем: необязательное поле не отличает «нет права» от
 * «сумма не заполнена», и первый же маппер, забывший ветку, отдал бы сумму
 * всем. Ветвление живёт в сервисе (В2c) с ЯВНЫМ маппером на каждом выходе.
 *
 * ─── МОДЕЛИ `Complex` НЕТ ─────────────────────────────────────────────────
 *
 * «Комплекс» — поле-группировка `Project.complexName` плюс фильтр (решение 12),
 * а не справочник. Схема значения — здесь же, рядом с договором, потому что
 * обе правки приезжают одной миграцией 048 и одним экраном карточки объекта.
 */
import { z } from 'zod';
import { ruEnum } from './lib/zod-ru';

// ─── Зеркало перечисления БД ──────────────────────────────────────────────

export const ContractStatus = {
  IN_PROGRESS: 'IN_PROGRESS',
  CLOSED: 'CLOSED',
  CANCELLED: 'CANCELLED',
} as const;
export type ContractStatus = (typeof ContractStatus)[keyof typeof ContractStatus];

export const CONTRACT_STATUS_LABEL: Readonly<Record<ContractStatus, string>> = Object.freeze({
  IN_PROGRESS: 'В работе',
  CLOSED: 'Закрыт',
  CANCELLED: 'Отменён',
});

// ─── Примитивы ────────────────────────────────────────────────────────────

const UuidSchema = z.string().uuid();
const IsoDateTimeSchema = z.string().datetime({ offset: true });
const IsoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата должна быть в формате ГГГГ-ММ-ДД');

/** Номер договора — 1..100 после обрезки, зеркало CHECK `contract_number_length`. */
const ContractNumberSchema = z
  .string()
  .trim()
  .min(1, 'Укажите номер договора')
  .max(100, 'Номер договора не длиннее 100 символов');

/**
 * Сумма договора — ₽ с копейками. Границы — зеркало `contract_sum_*`, то есть
 * дословный клон конвенции денег из миграции 033.
 *
 * `multipleOf(0.01)` намеренно НЕТ — но НЕ по той причине, что стояла здесь
 * прежде. Прежнее обоснование («на дробных двоичных значениях даёт ложные
 * отказы, 0.29 % 0.01 !== 0») ЛОЖНО и опровергнуто прогоном в круге ревью:
 * zod 3 считает `multipleOf` через масштабирование по числу знаков
 * (`floatSafeRemainder`), а не через `%`, и ложных отказов не даёт.
 *
 * Действующее обоснование: третий знак — это не ошибка ввода, а вставленное
 * значение из чужой системы, и отказывать форме на нём избыточно.
 *
 * ⚠️ СЛЕДСТВИЕ, КОТОРОЕ НАДО ЗНАТЬ ВСЛУХ: `sum: 1.005` схему проходит, а
 * `numeric(14,2)` в Postgres МОЛЧА округлит до `1.01`. Деньги меняются без
 * ошибки и без следа. Округление до копеек — обязанность сервиса В2c, ПЕРЕД
 * записью.
 *
 * ЧЕМ ИМЕННО ОКРУГЛЯТЬ — правка В2c, и прежний рецепт здесь был НЕВЕРЕН.
 * Стояло `Math.round(sum * 100) / 100`; приём считает в двоичной плавающей
 * точке, и умножение на 100 иногда СБИВАЕТ половину копейки вниз:
 * `1.005 * 100` — это 100.49999999999999, значит `Math.round` вернёт **1.00**,
 * а `numeric(14,2)` округлил бы до **1.01**. Лечение против «деньги меняются
 * молча» само меняло бы их молча, и в другую сторону.
 *
 * ⚠️ ЧИСЛО ЗДЕСЬ ПОДОБРАНО, А НЕ ВЗЯТО НАУГАД, И МЕНЯТЬ ЕГО НЕЛЬЗЯ.
 * Расхождение даёт НЕ всякая половинная копейка: `100.005 * 100` — ровно
 * 10000.5, и старый рецепт на нём отвечает 100.01, то есть ВЕРНО. Пример,
 * выбранный на глаз, дефекта не показывает — и хуже того, самоподтверждается:
 * усомнившийся проверит рецепт таким числом, увидит совпадение и решит, что
 * рецепт верен.
 *
 * ПРИЗНАК, А НЕ СПИСОК — потому что список сам собирают на глаз, и это уже
 * случилось дважды. Расхождение даёт значение, у которого произведение на сто
 * в двоичной арифметике оказывается СТРОГО НИЖЕ математической половины:
 * тогда `Math.round` уводит вниз, а десятичный счёт — вверх. Проверять надо
 * вычислением этого условия, а не подстановкой «какой-нибудь половины».
 *
 * Насколько это редко: во всём диапазоне до 5.00 таких значений ТРИДЦАТЬ ПЯТЬ.
 * Первые из них (ВЫЧИСЛЕНЫ, не подобраны): `0.145`, `0.285`, `0.565`, `0.575`,
 * `1.005`, `1.015`, `1.025`, `1.035`, `1.255`. Соседние по виду `2.005` и
 * `4.005` дают ровно 200.5 и 400.5 — и НЕ различают рецепты; первая редакция
 * этого перечня называла их пригодными, и это была третья по счёту встреча с
 * тем же классом — внутри лечения от него.
 *
 * Действует: `new Prisma.Decimal(sum).toDecimalPlaces(2, ROUND_HALF_UP)` —
 * `Decimal` для нецелого JS-числа разбирает его СТРОКОВОЕ представление, а не
 * двоичное значение, поэтому половина остаётся половиной и уходит вверх, как
 * у Postgres. Реализация — `apps/api/src/contract/contract.service.ts`,
 * функция `roundToKopecks`, тест — `apps/api/test/contract-e2e.spec.ts`.
 *
 * ─── ОТ ЧЕГО ЭТО ЗАЩИЩАЕТ НА САМОМ ДЕЛЕ ───────────────────────────────────
 *
 * ⚠️ НЕ ОТ ТОГО, ЧТО СКАЗАНО ВЫШЕ ПРО БАЗУ, и прежняя редакция обещала лишнее.
 * Убрать округление из сервиса вовсе — и третий знак всё равно исчезнет: его
 * съест `numeric(14,2)`, и съест ТОЧНО ТАК ЖЕ, половиной вверх. Проверено
 * прогоном (тест «база округляет так же»). Значит наша функция НЕ
 * предотвращает изменение суммы — она делает его ЯВНЫМ и в одном месте.
 *
 * Единственное, от чего она защищает, — от округления, расходящегося с базой,
 * то есть ровно от того, что и случилось со старым рецептом. Ценность в
 * согласии с базой, а не в её опережении. Формулировать иначе — значит снова
 * отправить следующего искать защиту там, где её нет.
 */
const ContractSumSchema = z
  .number()
  .nonnegative('Сумма не может быть отрицательной')
  .max(99_999_999_999.99, 'Сумма слишком велика');

/**
 * Название комплекса — 1..200 после обрезки, зеркало CHECK
 * `project_complex_name_length`. Пустая строка нормализуется в «нет комплекса»:
 * форма всегда присылает `''`, и без нормализации очистить поле было бы нечем.
 */
/**
 * Адрес/площадка объекта (ТЗ-5.3-a) — 1..500 после обрезки, зеркало CHECK
 * `project_address_length` из миграции 048.
 *
 * Схема заведена в В1, хотя маршрут правки объекта приезжает в В2c: колонка
 * уже существует, и до появления DTO она была бы открыта для записи вообще без
 * границы — ровно это и нашёл круг ревью. Пустая строка нормализуется в «нет
 * адреса» — как у `complexName`, и по той же причине.
 */
export const AddressSchema = z.preprocess((v) => {
  if (typeof v !== 'string') return v;
  const trimmed = v.trim();
  return trimmed.length === 0 ? null : trimmed;
}, z.string().min(1).max(500, 'Адрес не длиннее 500 символов').nullable());

/**
 * ─── Р-149 «А — СЧИТАТЬ ОДНИМ» (16.09.2026) ──────────────────────────────
 *
 * «ЖК Парковый», «жк парковый» и «ЖК  Парковый» (два пробела) — ОДИН комплекс.
 * Решение владельца: регистр и лишние пробелы не различаются.
 *
 * ⚠️ РАЗНЫЕ ПРАВИЛА ДЛЯ ХРАНЕНИЯ И ДЛЯ СРАВНЕНИЯ, И ЭТО НЕ НЕБРЕЖНОСТЬ.
 *   - ХРАНИМ как набрал человек, схлопнув лишь ПРОБЕЛЫ: регистр несёт смысл
 *     («ЖК» — аббревиатура), и приводить его к нижнему значило бы портить
 *     данные ради удобства сравнения;
 *   - СРАВНИВАЕМ по `normalizeComplexName` — без регистра и без лишних
 *     пробелов. По нему же группируются подсказки и отбирается список.
 *
 * Отсюда и «ключ» отдельной функцией: правило сравнения обязано быть ОДНИМ на
 * сервер (группировка `complexNames`, фильтр списка, ПОДСТАНОВКА существующего
 * написания при сохранении) и на клиент (подсказки формы и отбора). Второе его
 * описание разошлось бы с первым.
 *
 * ─── ГРАНИЦА КЛЮЧА НАЗВАНА ВСЛУХ ─────────────────────────────────────────
 *
 * Ключ снимает РОВНО ТРИ различия: краевые пробелы, повторяющиеся внутренние
 * пробелы и регистр. Ниже — то, что он НЕ склеивает, и это не недоделка, а
 * граница правила (проверяющему не заводить дефектом):
 *   - «ё» и «е» («Берёзовый» ≠ «Березовый»);
 *   - дефис против пробела («Северный-2» ≠ «Северный 2»), кавычки;
 *   - ЛАТИНСКИЕ двойники кириллицы: «ЖK» с латинской `K` — другой комплекс, и
 *     на экране он неотличим от кириллического;
 *   - НО форма записи Unicode различием НЕ является: и ключ, и хранимое
 *     значение приводятся к NFC. Вставленное с macOS «й» как «и» + U+0306
 *     (NFD) и набранное с клавиатуры «й» (NFC) — один комплекс. Без этой
 *     строки две визуально одинаковые строки жили бы как два написания, и
 *     объяснить это человеку было бы нечем.
 */
export function normalizeComplexName(value: string): string {
  return collapseComplexSpaces(value).toLocaleLowerCase('ru');
}

/**
 * `trim` + схлопывание ВНУТРЕННИХ пробелов + приведение к NFC; регистр не
 * трогается.
 *
 * NFC стои́т здесь, а не только в ключе, намеренно: приводится и то, что
 * СОХРАНЯЕТСЯ (через `ComplexNameSchema`). Иначе в базе лежало бы NFD-написание,
 * ключ которого сходится с NFC-написанием соседа, а предикат отбора
 * `complexName IN (…)` сравнивает строки БАЙТАМИ — и группа, склеенная ключом,
 * рассыпалась бы на запросе.
 */
export function collapseComplexSpaces(value: string): string {
  return value.normalize('NFC').trim().replace(/\s+/g, ' ');
}

/**
 * Написание, под которым значение ляжет в базу: если среди уже существующих
 * есть равное ПО КЛЮЧУ — берётся оно, иначе набранное (со схлопнутыми
 * пробелами).
 *
 * ⚠️ ЭТО НЕ КОСМЕТИКА. Без подстановки человек, набравший «жк парковый» при
 * живом «ЖК Парковый», завёл бы второе написание одного комплекса — ровно то,
 * что Р-149 запрещает. Сравнение ведётся по ключу, а подставляется ЧУЖОЕ
 * написание целиком.
 *
 * ⚠️ ПОРЯДОК `existing` — НЕСУЩИЙ, А НЕ ПРОИЗВОЛЬНЫЙ: берётся ПЕРВОЕ подошедшее,
 * и Р-149 говорит «первое заведённое». Вызывающий обязан подать написания в
 * порядке появления (сервер сортирует их по `createdAt` первого объекта, при
 * равенстве — по `id`). Подать их в алфавитном порядке значило бы молча сменить
 * правило представителя.
 */
export function canonicalComplexName(value: string, existing: readonly string[]): string {
  const collapsed = collapseComplexSpaces(value);
  if (collapsed.length === 0) return '';
  const key = normalizeComplexName(collapsed);
  return existing.find((e) => normalizeComplexName(e) === key) ?? collapsed;
}

export const ComplexNameSchema = z.preprocess((v) => {
  if (typeof v !== 'string') return v;
  // Р-149: пробелы схлопываются ЗДЕСЬ, на входе сервера, а не только в форме —
  // иначе бот, скрипт посева и прямой запрос заводили бы написания, которые
  // форма завести уже не может.
  const trimmed = collapseComplexSpaces(v);
  return trimmed.length === 0 ? null : trimmed;
}, z.string().min(1).max(200, 'Название комплекса не длиннее 200 символов').nullable());

const CONTRACT_DATES_MESSAGE = 'Срок договора не может быть раньше его даты';

// ─── Запросы на запись: договор ───────────────────────────────────────────

export const CreateContractRequestSchema = z
  .object({
    number: ContractNumberSchema,
    clientId: UuidSchema,
    sum: ContractSumSchema.optional(),
    status: z.nativeEnum(ContractStatus, ruEnum('Выберите статус договора')).optional(),
    date: IsoDateSchema.optional(),
    deadline: IsoDateSchema.optional(),
    note: z.string().trim().max(2000, 'Примечание не длиннее 2000 символов').optional(),
  })
  .strict()
  // Зеркало CHECK `contract_dates_valid` (048). Правило держит база; здесь оно
  // повторено, чтобы человек увидел понятный текст вместо 23514.
  .refine((v) => v.date === undefined || v.deadline === undefined || v.deadline >= v.date, {
    message: CONTRACT_DATES_MESSAGE,
    path: ['deadline'],
  });
export type CreateContractRequest = z.infer<typeof CreateContractRequestSchema>;

export const UpdateContractRequestSchema = z
  .object({
    version: z.number().int().nonnegative(),
    number: ContractNumberSchema.optional(),
    clientId: UuidSchema.optional(),
    sum: ContractSumSchema.nullable().optional(),
    status: z.nativeEnum(ContractStatus, ruEnum('Выберите статус договора')).optional(),
    date: IsoDateSchema.nullable().optional(),
    deadline: IsoDateSchema.nullable().optional(),
    note: z.string().trim().max(2000, 'Примечание не длиннее 2000 символов').nullable().optional(),
  })
  .strict()
  // Обе даты присланы вместе — сверяем здесь; присланная одна проверяется
  // сервером против сохранённой второй (схема второй не видит), а последним
  // рубежом стоит CHECK `contract_dates_valid`.
  .refine(
    (v) =>
      v.date === undefined ||
      v.date === null ||
      v.deadline === undefined ||
      v.deadline === null ||
      v.deadline >= v.date,
    { message: CONTRACT_DATES_MESSAGE, path: ['deadline'] },
  );
export type UpdateContractRequest = z.infer<typeof UpdateContractRequestSchema>;

/**
 * Тело ВОССТАНОВЛЕНИЯ договора (`POST /contracts/:id/restore`, Р-150) — только
 * версия. Заведена в Ф2 · В2c как тело архивации.
 *
 * ⚠️ АРХИВАЦИЯ ЭТУ СХЕМУ БОЛЬШЕ НЕ ИСПОЛЬЗУЕТ: её тело —
 * `ArchiveContractRequestSchema` ниже (версия плюс согласие
 * `detachProjectIds`). Здесь согласия нет и быть не может: восстановление
 * ничего не отвязывает и отвязанное при архивации обратно не привязывает.
 *
 * ОТДЕЛЬНАЯ схема, а не `UpdateContractRequestSchema` с одним полем: та
 * `.strict()`, но допускает и остальные поля, то есть восстановление приняло бы
 * заодно новую сумму. Здесь `.strict()` над одним полем — отказ.
 */
export const ContractVersionOnlyRequestSchema = z
  .object({ version: z.number().int().nonnegative() })
  .strict();
export type ContractVersionOnlyRequest = z.infer<typeof ContractVersionOnlyRequestSchema>;

/**
 * Тело архивации — версия плюс СОГЛАСИЕ НА ИДЕНТИФИКАТОРЫ, а не на признак.
 *
 * ⚠️ ИМЕНОВАТЬ ЭТО «СОГЛАСИЕМ ИМЕНАМИ» НЕВЕРНО, и слово поправлено кругом
 * ревью: на проводе идут `projectIds`, имён объектов здесь нет ни в отказе, ни
 * в согласии. Инвариант «человек соглашается ровно на то, что ему показали»
 * держится СОСТАВОМ множества, а не названиями, — искать имена объектов в
 * полосе диалога не надо, там ЧИСЛО.
 *
 * ⚠️ ПРЕЖНЕЕ СОГЛАСИЕ (`?detach=true`) СНЯТО КРУГОМ РЕВЬЮ, И ВОТ ПОЧЕМУ.
 * Диалог архивации устроен в два шага: первый запрос идёт без согласия, сервер
 * отказывает и называет ЧИСЛО живых объектов, человек видит его и нажимает
 * второй раз. Признак `detach=true` говорил серверу «отвязывай что найдёшь»,
 * а находил он объекты ЗАНОВО — то есть объект, привязанный между двумя
 * нажатиями, отвязывался тоже, хотя в показанное человеку число не входил.
 * Версия договора этого не ловит: она про строку договора, а менялись объекты.
 *
 * Теперь согласие — СПИСОК ИДЕНТИФИКАТОРОВ из отказа первого шага. Сервер
 * сверяет его с текущим составом и при расхождении отвечает 409
 * `CONTRACT_PROJECTS_CHANGED`, называя новый состав: человек соглашается
 * ровно на то, что ему показали.
 *
 * ⚠️ ОТСУТСТВИЕ ПОЛЯ — ЭТО «СОГЛАСИЯ НЕТ», А ПУСТОЙ СПИСОК — «СОГЛАСЕН, ЧТО
 * ОТВЯЗЫВАТЬ НЕЧЕГО». Разница видна на гонке: договор без объектов
 * архивируется с пустым списком, а если объект появился — тот же запрос
 * получит 409 вместо тихой отвязки.
 *
 * Потолок списка защитный: реальный состав — десятки, тысяча идентификаторов
 * означает не рабочий случай, а подложенное тело.
 */
export const ArchiveContractRequestSchema = z
  .object({
    version: z.number().int().nonnegative(),
    detachProjectIds: z.array(UuidSchema).max(1000).optional(),
  })
  .strict();
export type ArchiveContractRequest = z.infer<typeof ArchiveContractRequestSchema>;

/**
 * Разбор строки запроса списка договоров — Ф2 · В2c.
 *
 * `status` здесь — про АРХИВНОСТЬ, а не про `ContractStatus`. Имена разные
 * намеренно (`archived` / `active`, а не `CLOSED`): у договора две независимые
 * оси — рабочее состояние (`ContractStatus`) и снятие со справочника
 * (`archivedAt`), и совпадение имён свело бы их в одну в первой же форме.
 *
 * Отсутствие параметра = только действующие. Умолчание НЕ «все»: справочник
 * кормит выпадающий список выбора договора у объекта, и архивный договор там
 * выбирать нельзя.
 */
export const ContractListQuerySchema = z
  .object({
    status: z.enum(['active', 'archived', 'all']).optional(),
    /**
     * Р-151 «А — ТОЛЬКО ДЕЙСТВУЮЩИЕ» (16.09.2026) — отбор по РАБОЧЕМУ
     * состоянию, на сервере.
     *
     * ⚠️ ВТОРОЙ ПАРАМЕТР, А НЕ ЧЕТВЁРТОЕ ЗНАЧЕНИЕ `status`, и это ровно то
     * различие, о котором говорит докблок выше: у договора ДВЕ независимые оси
     * — снятие со справочника (`archivedAt`) и рабочее состояние
     * (`ContractStatus`). Значение `in_progress` рядом с `archived` свело бы их
     * в одну ось, и «действующий закрытый договор» стало бы нечем выразить.
     *
     * Решение говорит «отбор — на сервере, а не фильтром на клиенте по полному
     * списку»: список выбора договора у объекта не должен получать строки,
     * которые ему запрещено предлагать.
     *
     * Оси перемножаются: `?status=active&state=IN_PROGRESS` — не архивный И в
     * работе. Отсутствие параметра — все состояния, как было до Р-151.
     */
    state: z.nativeEnum(ContractStatus, ruEnum('Недопустимое состояние договора')).optional(),
  })
  .strict();
export type ContractListQuery = z.infer<typeof ContractListQuerySchema>;

// ─── Ответы: договор ──────────────────────────────────────────────────────

/** Общая часть — то, что видно ЛЮБОМУ, кто вообще видит договор. */
export const ContractResponseSchema = z.object({
  id: UuidSchema,
  number: z.string(),
  clientId: UuidSchema,
  clientName: z.string(),
  status: z.nativeEnum(ContractStatus),
  date: IsoDateSchema.nullable(),
  deadline: IsoDateSchema.nullable(),
  note: z.string().nullable(),
  /** Сколько объектов привязано — нужно, чтобы объяснить отказ архивации
   *  (422 `CONTRACT_HAS_PROJECTS`) числом, а не общими словами. */
  projectCount: z.number().int().nonnegative(),
  version: z.number().int().nonnegative(),
  archivedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type ContractResponse = z.infer<typeof ContractResponseSchema>;

/**
 * Расширенная форма — ТОЛЬКО для ADMIN и acts-as-GIP. Отдельный тип, а не
 * необязательное поле: так «забыл ветку маппера» становится ошибкой типа, а не
 * утечкой суммы.
 */
export const ContractWithSumResponseSchema = ContractResponseSchema.extend({
  sum: z.number().nullable(),
});
export type ContractWithSumResponse = z.infer<typeof ContractWithSumResponseSchema>;

/**
 * Ответ архивации — Ф2 · В2c.
 *
 * `detachedProjectCount` возвращается ВСЕГДА, в том числе нулём: без согласия
 * (`detachProjectIds`) архивация живого договора вообще не проходит (422
 * `CONTRACT_HAS_PROJECTS`), значит ноль означает «объектов не было», а не
 * «отвязать не удалось». Число нужно человеку: он нажал «отвязать» и обязан
 * увидеть, скольких объектов это коснулось.
 */
export const ArchiveContractResponseSchema = z.object({
  ok: z.literal(true),
  detachedProjectCount: z.number().int().nonnegative(),
});
export type ArchiveContractResponse = z.infer<typeof ArchiveContractResponseSchema>;

// ─── Ссылки на исходные данные объекта (049; UI — В11) ────────────────────

/**
 * Управляющие символы (C0, DEL, C1) в тексте, который поедет в `TEXT`.
 *
 * ⚠️ ЭТО НЕ КОСМЕТИКА, А ГРАНИЦА МЕЖДУ 400 И 500. `z.string().url()` нулевой
 * байт ПРОПУСКАЕТ (`new URL('https://example.org/\u0000')` разбирается без
 * ошибки), а `TEXT` в Postgres его не принимает вовсе: запись падает
 * `22021 invalid byte sequence for encoding "UTF8": 0x00` уже за пределами
 * всех прикладных проверок, и фильтр отказов, не узнав ни `P2025`, ни `23514`,
 * отдаёт 500. Пользовательский ввод не должен уметь превращаться в отказ
 * сервера — тот же довод, по которому фильтр переводит 40001/P2034 в 409, а не
 * роняет запрос.
 *
 * Проверка БЕЗ регулярного выражения намеренно: литерал `\u0000` в регулярке
 * отвергается правилом `no-control-regex`, а глушить правило ради одной строки
 * значило бы завести исключение там, где обычный цикл читается не хуже.
 */
function hasControlChars(value: string): boolean {
  for (const ch of value) {
    const code = ch.codePointAt(0);
    if (code === undefined) continue;
    if (code <= 0x1f || (code >= 0x7f && code <= 0x9f)) return true;
  }
  return false;
}

/**
 * URL исходных данных.
 *
 * СХЕМУ ПРОВЕРЯЕТ ЭТА СТРОКА, А НЕ БАЗА. В миграции 049 CHECK-а на схему нет
 * намеренно: регулярное выражение в CHECK выглядит защитой, но `javascript:` с
 * ведущими пробелами или в другом регистре пропустит так же, как плохая
 * клиентская проверка, — зато отказ даст непонятным 23514. Настоящих рубежей
 * два: `z.string().url()` + явный список схем здесь и проверка ПЕРЕД
 * подстановкой в `href` на рендере — `isSafeSourceLinkUrl` ниже, место вызова
 * `apps/web/src/pages/projects/components/project-source-links-panel.tsx`.
 *
 * ⚠️ ВТОРОЙ РУБЕЖ НУЖЕН ИМЕННО ПОТОМУ, ЧТО ПЕРВЫЙ СТОИТ НА ВХОДЕ API. В строку
 * `SourceLink.url` можно попасть мимо этой схемы: посевом (`demo-seed.ts` пишет
 * Prisma напрямую), ручным SQL при разборе инцидента, будущим импортом. Без
 * проверки на выводе такая строка доехала бы до `href` как есть.
 *
 * `data:` и `javascript:` отвергаются ЯВНО, а не «всё, что не https»: список
 * разрешённого читается однозначно, а отрицательный список — нет.
 */
const SourceLinkUrlSchema = z
  .string()
  .trim()
  .min(1, 'Укажите ссылку')
  .max(2000, 'Ссылка не длиннее 2000 символов')
  .url('Ссылка должна быть полным адресом, например https://…')
  .refine((v) => /^https?:\/\//i.test(v), {
    // ТЕКСТ НАЗЫВАЕТ ТОЛЬКО `https://`, хотя проверка пропускает и обычный
    // незащищённый адрес. Это не неточность: совет обязан быть ВЫПОЛНИМ
    // (адрес с `https://` проходит всегда), а называть второй вариант незачем —
    // никого не надо уговаривать перейти на менее защищённый.
    //
    // Заодно снимается отказ линта `aris/no-machine-lexeme`: его образец
    // `\bHTTP\b` срабатывает на слове `http` перед двоеточием, но не на
    // `https`. Формулировка исправлена, а исключение в `eslint.config.mjs` НЕ
    // заводилось: список исключений там короткий намеренно и пополняется после
    // ревью, а не ради зелёного прогона.
    message: 'Ссылка должна начинаться с https://',
  })
  // ⚠️ ПОРЯДОК ВЫНУЖДЕННЫЙ, А НЕ ВКУСОВОЙ: `.refine` возвращает `ZodEffects`, у
  // которого метода `.url()` уже нет, поэтому проверка управляющих символов
  // стоит ПОСЛЕ разбора адреса, а не перед ним. На отказ это не влияет
  // (отвергают обе), влияет только на то, чей текст увидит человек.
  .refine((v) => !hasControlChars(v), {
    message: 'Ссылка содержит недопустимые символы',
  });

/**
 * ВТОРОЙ РУБЕЖ — ПЕРЕД ПОДСТАНОВКОЙ В `href`.
 *
 * Схема та же, что на входе API: своей копии проверки на рендере нет намеренно,
 * потому что две копии расходятся, а разойдясь, дают ровно тот случай, ради
 * которого проверка и заведена. Функция, а не экспорт схемы, — чтобы место
 * вызова читалось решением («адрес безопасен»), а не разбором.
 *
 * Отвергнутый адрес НЕ ПРЯЧЕТСЯ: панель показывает его текстом с пометкой.
 * Спрятать строку значило бы скрыть от человека то, что в объекте уже лежит.
 */
export function isSafeSourceLinkUrl(value: unknown): boolean {
  return SourceLinkUrlSchema.safeParse(value).success;
}

const SourceLinkLabelSchema = z.preprocess(
  (v) => {
    if (typeof v !== 'string') return v;
    const trimmed = v.trim();
    return trimmed.length === 0 ? undefined : trimmed;
  },
  z
    .string()
    .min(1)
    .max(200, 'Подпись не длиннее 200 символов')
    .refine((v) => !hasControlChars(v), { message: 'Подпись содержит недопустимые символы' })
    .optional(),
);

export const CreateSourceLinkRequestSchema = z
  .object({
    url: SourceLinkUrlSchema,
    label: SourceLinkLabelSchema,
  })
  .strict();
export type CreateSourceLinkRequest = z.infer<typeof CreateSourceLinkRequestSchema>;

/**
 * Подпись на ПРАВКЕ — не та же схема, что на создании, и это не дубль.
 *
 * У создания пустая строка значит «подписи нет» и сворачивается в `undefined`:
 * поля в строке просто не будет. У правки то же `undefined` означает «не
 * трогать», и свернуть в него пустую строку — значит отнять у человека
 * единственный способ ПОДПИСЬ СНЯТЬ. Поэтому здесь пустая строка сворачивается
 * в `null` («снять»), а отсутствие ключа остаётся «не трогать». Одна схема на
 * оба случая не различила бы эти два намерения вовсе.
 */
const SourceLinkLabelUpdateSchema = z.preprocess(
  (v) => {
    if (typeof v !== 'string') return v;
    const trimmed = v.trim();
    return trimmed.length === 0 ? null : trimmed;
  },
  z
    .string()
    .min(1)
    .max(200, 'Подпись не длиннее 200 символов')
    .refine((v) => !hasControlChars(v), { message: 'Подпись содержит недопустимые символы' })
    .nullable(),
);

/**
 * `PATCH /projects/:projectId/source-links/:id`.
 *
 * `version` ОБЯЗАТЕЛЕН — модель в `DEFAULT_LOCKED_MODELS`, и правка уходит
 * штатным `update` с `where.version`; без него расширение оптимистичной
 * блокировки нечего было бы сверять, и «последний записавший победил»
 * вернулось бы молча.
 *
 * Хотя бы одно изменяемое поле сверх `version` обязательно: пустой `PATCH`
 * поднял бы `version` и написал строку в журнал, ничего не изменив.
 */
export const UpdateSourceLinkRequestSchema = z
  .object({
    version: z.number().int().nonnegative(),
    url: SourceLinkUrlSchema.optional(),
    label: SourceLinkLabelUpdateSchema.optional(),
  })
  .strict()
  .refine((dto) => dto.url !== undefined || dto.label !== undefined, {
    message: 'Нет изменений для сохранения',
  });
export type UpdateSourceLinkRequest = z.infer<typeof UpdateSourceLinkRequestSchema>;

/**
 * `POST /projects/:projectId/source-links/:id/archive` — тело только с
 * `version`. Физического удаления у ссылок НЕТ: архивация сохраняет строку, а
 * `deleteMany` был бы аудит-слеп (`CLAUDE.md`, «Данные, аудит и права»).
 */
export const ArchiveSourceLinkRequestSchema = z
  .object({ version: z.number().int().nonnegative() })
  .strict();
export type ArchiveSourceLinkRequest = z.infer<typeof ArchiveSourceLinkRequestSchema>;

export const SourceLinkResponseSchema = z.object({
  id: UuidSchema,
  projectId: UuidSchema,
  /**
   * ⚠️ ЗДЕСЬ `z.string()`, А НЕ `SourceLinkUrlSchema`, И ЭТО РЕШЕНИЕ, А НЕ
   * НЕДОСМОТР. Разбор ответа обязан УМЕТЬ ПРЕДСТАВИТЬ то, что уже лежит в
   * колонке, — включая строку, положенную туда мимо zod (посев, ручной SQL,
   * импорт). Поставь здесь рубеж адреса — и одна такая строка уронит разбор
   * ВСЕГО списка: панель показала бы ошибку вместо ссылок, то есть дефект
   * отображения одной строки превратился бы в отказ всей поверхности. На
   * правке было бы хуже: человек чинит подпись у legacy-строки, сервер
   * отвечает 200, а клиент считает ответ невалидным — успешная запись
   * выглядит неудавшейся.
   *
   * Рубеж стоит там, где он и должен стоять, — ПЕРЕД подстановкой в `href`
   * (`isSafeSourceLinkUrl`); отвергнутый адрес показывается текстом с
   * пометкой, а не прячется.
   */
  url: z.string(),
  label: z.string().nullable(),
  addedById: UuidSchema,
  addedByName: z.string(),
  version: z.number().int().nonnegative(),
  archivedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type SourceLinkResponse = z.infer<typeof SourceLinkResponseSchema>;

/**
 * `GET /projects/:projectId/source-links` — список АКТИВНЫХ ссылок объекта.
 *
 * Конверт `{ data }`, как у разделов и расходов: место под будущие поля
 * (счётчик, признак «есть архивные») остаётся, и клиент не переписывается,
 * когда оно появится.
 *
 * ─── `canWrite` СЧИТАЕТ СЕРВЕР, А НЕ ЭКРАН ────────────────────────────────
 *
 * Поле отвечает на вопрос «покажет ли панель кнопки», и считается оно ТЕМ ЖЕ
 * предикатом, что отвергает запись (`canWriteProject` ∧ объект не в архиве).
 * Клиентское зеркало предиката — а панель жила именно на нём — расходится с
 * сервером молча и в обе стороны: помощник чужого ГИПа кнопок не видел, хотя
 * сервер отдаёт ему 201, а любое будущее сужение прав (Ф8) оставило бы кнопки
 * на экране, каждая из которых ведёт в 403. Один источник вместо двух.
 */
export const SourceLinkListResponseSchema = z.object({
  data: z.array(SourceLinkResponseSchema),
  canWrite: z.boolean(),
});
export type SourceLinkListResponse = z.infer<typeof SourceLinkListResponseSchema>;
